import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { persistFiltersForView } from '@shared/filter-persistence';
import type { ViewType } from '@shared/filter-persistence';
import { deserialize, serialize } from '@shared/filter-serializer';

/**
 * Maps route pathnames to ViewType identifiers used by the filter-persistence module.
 */
function getViewTypeFromPath(pathname: string): ViewType {
  const normalized = pathname.replace(/\/$/, '') || '/';

  if (normalized === '/inventory' || normalized.startsWith('/inventory/')) {
    return 'inventory';
  }
  if (normalized === '/security' || normalized.startsWith('/security/')) {
    return 'security';
  }
  if (normalized === '/metrics' || normalized.startsWith('/metrics/')) {
    return 'metrics';
  }
  if (normalized === '/relationships' || normalized.startsWith('/relationships/')) {
    return 'relationships';
  }
  return 'dashboard';
}

/**
 * Hook that persists filter state across view navigation.
 *
 * On navigation to a new view:
 * 1. Reads current filter state from URL search params
 * 2. Applies filter-persistence logic to determine which filters carry over
 * 3. Updates the URL with the filtered subset
 *
 * Rules (implemented by @shared/filter-persistence):
 * - Provider, region, project/account persist in ALL views
 * - Severity persists only in inventory and security views
 * - Resource type is discarded in metrics view
 * - Search is always cleared on navigation
 *
 * This hook should be used in NavigationShell or a higher-level component
 * that wraps all routes.
 */
export function useFilterPersistence(): void {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousPathRef = useRef<string>(location.pathname);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // Only apply persistence logic when the pathname actually changes
    if (currentPath === previousPath) {
      return;
    }

    const previousView = getViewTypeFromPath(previousPath);
    const targetView = getViewTypeFromPath(currentPath);

    // Update the ref to track the new path
    previousPathRef.current = currentPath;

    // Only apply filter persistence when navigating between different views
    if (previousView === targetView) {
      return;
    }

    // Read current filters from URL
    const currentFilters = deserialize(searchParams);

    // Apply persistence rules for the target view
    const persistedFilters = persistFiltersForView(currentFilters, targetView);

    // Serialize the persisted filters to URL params
    const newParams = serialize(persistedFilters);

    // Only update if the params actually changed
    const currentParamsStr = searchParams.toString();
    const newParamsStr = newParams.toString();

    if (currentParamsStr !== newParamsStr) {
      setSearchParams(newParams, { replace: true });
    }
  }, [location.pathname, searchParams, setSearchParams]);
}

export { getViewTypeFromPath };
